/**
 * 適職診断の結果の「私にもなれる？どれくらい？」の札に出す、学習パスの章と項目の数。
 *
 * 数は本番の学習パス (ゆめさくの既定のパス) から tools/curriculum/job-diagnosis-path/pull.ts が
 * 読み取りだけで集め、job-diagnosis-path.generated.ts に書く。ここは章の選び方と、出す形だけを持つ。
 * 何か月で届くかは約束しない。出すのは章の名前と項目の数だけ。
 */
import { CHAPTERS, PATH_FETCHED_AT, PATH_TOTAL_ITEMS } from "./job-diagnosis-path.generated";
import type { RoleId } from "./job-type-diagnosis";

export type PathChapter = {
  /** 章の番号 (第6章 なら 6) */
  no: number;
  /** 本番の章の名前そのまま */
  title: string;
  /** 公開中の項目の数。章の始まりの印 (milestone) は数えない */
  items: number;
};

/** 本番から読んだ 1 章。pull.ts が受け取る形 */
export type RawSection = { id: string; title: string; displayOrder: number; lpCourseId: string | null };
export type RawItem = {
  sectionId: string | null;
  itemType: string;
  isPublished: boolean;
  lpCourseId: string | null;
};

/**
 * 既定のパス (lpCourseId が無いもの) の公開中の項目を章ごとに数える。
 * 章の始まりの印 (milestone) は学ぶ項目ではないので数えない。章の名前が「第N章」で始まらない区切り
 * (卒業など) は章に数えないが、その中の項目は全体の数に入れる
 */
export function summarizeChapters(
  sections: readonly RawSection[],
  items: readonly RawItem[],
): { chapters: PathChapter[]; totalItems: number } {
  const counted = items.filter((i) => i.lpCourseId === null && i.isPublished && i.itemType !== "milestone");
  const inDefault = sections.filter((s) => s.lpCourseId === null);
  const ids = new Set(inDefault.map((s) => s.id));
  const chapters = [...inDefault]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .flatMap((s) => {
      const m = /^第(\d+)章/.exec(s.title);
      if (!m) return [];
      return [{ no: Number(m[1]), title: s.title, items: counted.filter((i) => i.sectionId === s.id).length }];
    });
  return { chapters, totalItems: counted.filter((i) => i.sectionId !== null && ids.has(i.sectionId)).length };
}

/**
 * 職種ごとに、最初に触る章。学習パスの並び順で書く。
 * 合う章が 1 つしか無い職種は 1 つだけにし、合わない章で埋めない (マークアップは第11章の前半の HTML と CSS だけ)。
 * セキュリティだけを学ぶ章は無いので、守りの土台になる Linux (権限と SSH) と
 * コンピュータサイエンス (ネットワーク) を出し、本番の第9章にある Webセキュリティの項目を note で名指す
 */
export const ROLE_PATH_CHAPTERS: Record<RoleId, { chapters: readonly number[]; note?: string }> = {
  backend: { chapters: [6, 8, 9] },
  frontend: { chapters: [8, 11, 12] },
  infrastructure: { chapters: [4, 5, 15] },
  security: { chapters: [4, 5], note: "セキュリティは、|第9章の|「Webセキュリティ基礎|（XSS・CSRF・SQLインジェクション）」|から触れます" },
  data: { chapters: [6, 7, 16] },
  ai: { chapters: [3, 7, 13] },
  "web-production": { chapters: [11], note: "HTML と CSS は、|この章の前半で|学びます" },
  "ai-work": { chapters: [3, 7] },
};

/** 狭い札に出す章の名前。「第6章 データベース・SQL編」を「データベース・SQL」にする */
export function shortChapterName(title: string): string {
  return title.replace(/^第\d+章\s*/, "").replace(/編$/, "");
}

export type RolePathSummary = {
  chapters: PathChapter[];
  /** 札の下の 1 行。| は折ってよい所で、塊の途中では折らない */
  note?: string;
  /** 学習パス全体の章の数と項目の数 */
  totalChapters: number;
  totalItems: number;
  fetchedAt: string;
};

export function rolePathSummary(role: RoleId): RolePathSummary {
  const pick = ROLE_PATH_CHAPTERS[role];
  const chapters = pick.chapters.map((no) => {
    const ch = CHAPTERS.find((c) => c.no === no);
    if (!ch) throw new Error(`学習パスに第${no}章が無い (${role})`);
    return ch;
  });
  return {
    chapters,
    ...(pick.note ? { note: pick.note } : {}),
    totalChapters: CHAPTERS.length,
    totalItems: PATH_TOTAL_ITEMS,
    fetchedAt: PATH_FETCHED_AT,
  };
}
